import {
  RESOURCE_BINDINGS,
  type ResourceBindingDefinition,
} from '@seen/core/resourceBindingRegistry'
import { Plugin } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { Extension } from '@tiptap/react'
import Fuse from 'fuse.js'

interface TemplateVariableOptions {
  isTemplate?: boolean
  // Behind the structuredCapture flag; off, no picker is installed.
  enabled?: boolean
}

interface ActiveTemplateVariable {
  from: number
  to: number
  query: string
}

const BINDING_SEARCH = new Fuse<ResourceBindingDefinition>(
  [...RESOURCE_BINDINGS],
  {
    keys: ['label', 'description', 'id', 'token'],
    threshold: 0.4,
  }
)

export function templateVariableSuggestions(
  query: string
): ResourceBindingDefinition[] {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) {
    return [...RESOURCE_BINDINGS].slice(0, 10)
  }
  const words = trimmed.split(/\s+/)
  const directMatches = RESOURCE_BINDINGS.filter((binding) => {
    const searchable = [
      binding.label,
      binding.description,
      binding.id,
      'token' in binding ? binding.token : '',
    ]
      .join(' ')
      .toLowerCase()
    return words.every((word) => searchable.includes(word))
  })
  const fuzzyMatches = BINDING_SEARCH.search(trimmed).map(
    (result) => result.item
  )
  return [...new Set([...directMatches, ...fuzzyMatches])].slice(0, 10)
}

// The `{{` picker in the template editor. It inserts `{{binding.id}}` text,
// which the registry variable node converts into a deferred variable; filling
// in happens where the note is read, never here.
export const TemplateVariableNode = Extension.create<TemplateVariableOptions>({
  name: 'templateVariable',

  addOptions() {
    return {
      isTemplate: false,
      enabled: false,
    }
  },

  addProseMirrorPlugins() {
    if (!this.options.isTemplate || !this.options.enabled) {
      return []
    }
    let picker: TemplateVariablePicker | undefined
    return [
      new Plugin({
        view: (view) => {
          picker = new TemplateVariablePicker(view)
          return {
            update: (nextView) => picker?.update(nextView),
            destroy: () => picker?.destroy(),
          }
        },
        props: {
          handleKeyDown: (_view, event) =>
            picker?.handleKeyDown(event) ?? false,
        },
      }),
    ]
  },
})

class TemplateVariablePicker {
  private view: EditorView
  private element: HTMLDivElement | null = null
  private active: ActiveTemplateVariable | null = null
  private suggestions: ResourceBindingDefinition[] = []
  private selectedIndex = 0

  constructor(view: EditorView) {
    this.view = view
    this.update(view)
  }

  update(view: EditorView): void {
    this.view = view
    const active = activeTemplateVariable(view)
    if (!active) {
      this.close()
      return
    }
    this.active = active
    this.suggestions = templateVariableSuggestions(active.query)
    this.selectedIndex = Math.min(
      this.selectedIndex,
      Math.max(this.suggestions.length - 1, 0)
    )
    this.render()
  }

  handleKeyDown(event: KeyboardEvent): boolean {
    if (!this.element || this.suggestions.length === 0) {
      return false
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length
      this.render()
      return true
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.selectedIndex =
        (this.selectedIndex - 1 + this.suggestions.length) %
        this.suggestions.length
      this.render()
      return true
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      this.select(this.suggestions[this.selectedIndex])
      return true
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      return true
    }
    return false
  }

  destroy(): void {
    this.close()
  }

  private render(): void {
    if (!this.active) {
      return
    }
    this.element ??= this.createElement()
    this.element.replaceChildren()
    if (this.suggestions.length === 0) {
      const empty = document.createElement('p')
      empty.className = 'px-3 py-2 text-sm text-muted-foreground'
      empty.textContent = 'No matching properties'
      this.element.append(empty)
    } else {
      for (const [index, binding] of this.suggestions.entries()) {
        const option = document.createElement('button')
        option.type = 'button'
        option.role = 'option'
        option.ariaSelected = String(index === this.selectedIndex)
        option.className =
          'flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent aria-selected:bg-accent'
        const label = document.createElement('span')
        label.className = 'font-medium'
        label.textContent = binding.label
        const detail = document.createElement('span')
        detail.className = 'text-xs text-muted-foreground'
        detail.textContent = `${binding.id} · ${binding.description}`
        option.append(label, detail)
        option.addEventListener('mousedown', (event) => {
          event.preventDefault()
          this.select(binding)
        })
        this.element.append(option)
      }
    }
    const coords = this.view.coordsAtPos(this.active.to)
    this.element.style.left = `${coords.left}px`
    this.element.style.top = `${coords.bottom + 4}px`
  }

  private createElement(): HTMLDivElement {
    const element = document.createElement('div')
    element.role = 'listbox'
    element.ariaLabel = 'Template properties'
    element.className =
      'fixed z-50 max-h-80 w-96 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md'
    document.body.append(element)
    return element
  }

  private select(binding: ResourceBindingDefinition | undefined): void {
    if (!binding || !this.active) {
      return
    }
    this.view.dispatch(
      this.view.state.tr.insertText(
        `{{${binding.id}}}`,
        this.active.from,
        this.active.to
      )
    )
    this.view.focus()
    this.close()
  }

  private close(): void {
    this.element?.remove()
    this.element = null
    this.active = null
    this.suggestions = []
    this.selectedIndex = 0
  }
}

function activeTemplateVariable(
  view: EditorView
): ActiveTemplateVariable | null {
  const { selection } = view.state
  if (!selection.empty) {
    return null
  }
  const { $from } = selection
  const text = $from.parent.textBetween(0, $from.parentOffset, '\0', '\0')
  const match = text.match(/{{([^{}\n]*)$/)
  if (!match) {
    return null
  }
  return {
    from: selection.from - match[0].length,
    to: selection.from,
    query: match[1] ?? '',
  }
}
